/**
 * Numerical primitives for the engine: distribution quantiles at double
 * precision. The parity targets (brief 14.1) require interval endpoints
 * accurate to 1e-6 absolute against R's qt/qbeta/qnorm, so these routines
 * aim for ~1e-13.
 *
 * - betaCdf / betaQuantile: regularized incomplete beta by continued
 *   fraction (Lentz), inverted by bracketed Newton iteration.
 * - tQuantile: reduced to betaQuantile through the standard t/beta relation.
 * - normalQuantile: bracketed Newton inversion of the normal CDF, which is
 *   computed from the regularized incomplete gamma (series / continued
 *   fraction) at a = 1/2.
 */
/** Log gamma (Lanczos), used by the incomplete beta and gamma. */
export function logGamma(x) {
    const cof = [
        0.99999999999980993, 676.5203681218851, -1259.1392167224028,
        771.32342877765313, -176.61502916214059, 12.507343278686905,
        -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7,
    ];
    if (x < 0.5) {
        return Math.log(Math.PI / Math.sin(Math.PI * x)) - logGamma(1 - x);
    }
    const z = x - 1;
    let acc = cof[0];
    for (let i = 1; i < cof.length; i++)
        acc += cof[i] / (z + i);
    const t = z + 7.5;
    return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(acc);
}
/** Regularized lower incomplete gamma P(a, x): series expansion. */
function gammaPSeries(a, x) {
    let term = 1 / a;
    let sum = term;
    let ap = a;
    for (let n = 0; n < 1000; n++) {
        ap += 1;
        term *= x / ap;
        sum += term;
        if (Math.abs(term) < Math.abs(sum) * 1e-16)
            break;
    }
    return sum * Math.exp(-x + a * Math.log(x) - logGamma(a));
}
/** Regularized upper incomplete gamma Q(a, x): continued fraction. */
function gammaQFraction(a, x) {
    const FPMIN = 1e-300;
    let b = x + 1 - a;
    let c = 1 / FPMIN;
    let d = 1 / b;
    let h = d;
    for (let i = 1; i < 1000; i++) {
        const an = -i * (i - a);
        b += 2;
        d = an * d + b;
        if (Math.abs(d) < FPMIN)
            d = FPMIN;
        c = b + an / c;
        if (Math.abs(c) < FPMIN)
            c = FPMIN;
        d = 1 / d;
        const del = d * c;
        h *= del;
        if (Math.abs(del - 1) < 1e-16)
            break;
    }
    return Math.exp(-x + a * Math.log(x) - logGamma(a)) * h;
}
/** Regularized lower incomplete gamma P(a, x). */
function gammaP(a, x) {
    if (x <= 0)
        return 0;
    if (x < a + 1)
        return gammaPSeries(a, x);
    return 1 - gammaQFraction(a, x);
}
/** Standard normal CDF, through erf(z) = P(1/2, z^2) with sign. */
export function normalCdf(x) {
    const g = gammaP(0.5, (x * x) / 2);
    return x >= 0 ? 0.5 * (1 + g) : 0.5 * (1 - g);
}
/**
 * Inverse standard normal CDF by bracketed Newton inversion of normalCdf.
 * Used for the z critical value of brief 9.3 (z = 1.959964 at 95%).
 */
export function normalQuantile(p) {
    if (p <= 0)
        return -Infinity;
    if (p >= 1)
        return Infinity;
    let lo = -40;
    let hi = 40;
    let x = 0;
    let best = x;
    let bestErr = Infinity;
    for (let iter = 0; iter < 300; iter++) {
        const cdf = normalCdf(x);
        const err = Math.abs(cdf - p);
        if (err < bestErr) {
            bestErr = err;
            best = x;
        }
        if (err <= 1e-14)
            return x;
        if (cdf < p)
            lo = Math.max(lo, x);
        else
            hi = Math.min(hi, x);
        if (hi - lo < 1e-14)
            return (lo + hi) / 2;
        const pdf = Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
        let next = pdf > 0 ? x - (cdf - p) / pdf : NaN;
        if (!(next > lo && next < hi) || iter % 4 === 3)
            next = (lo + hi) / 2;
        x = next;
    }
    return best;
}
/** Continued fraction for the incomplete beta (Lentz's algorithm). */
function betaContinuedFraction(a, b, x) {
    const MAXIT = 200;
    const EPS = 3e-16;
    const FPMIN = 1e-300;
    const qab = a + b;
    const qap = a + 1;
    const qam = a - 1;
    let c = 1;
    let d = 1 - (qab * x) / qap;
    if (Math.abs(d) < FPMIN)
        d = FPMIN;
    d = 1 / d;
    let h = d;
    for (let m = 1; m <= MAXIT; m++) {
        const m2 = 2 * m;
        let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2));
        d = 1 + aa * d;
        if (Math.abs(d) < FPMIN)
            d = FPMIN;
        c = 1 + aa / c;
        if (Math.abs(c) < FPMIN)
            c = FPMIN;
        d = 1 / d;
        h *= d * c;
        aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2));
        d = 1 + aa * d;
        if (Math.abs(d) < FPMIN)
            d = FPMIN;
        c = 1 + aa / c;
        if (Math.abs(c) < FPMIN)
            c = FPMIN;
        d = 1 / d;
        const del = d * c;
        h *= del;
        if (Math.abs(del - 1) < EPS)
            break;
    }
    return h;
}
/** Regularized incomplete beta I_x(a, b). */
export function betaCdf(x, a, b) {
    if (x <= 0)
        return 0;
    if (x >= 1)
        return 1;
    const bt = Math.exp(logGamma(a + b) -
        logGamma(a) -
        logGamma(b) +
        a * Math.log(x) +
        b * Math.log(1 - x));
    if (x < (a + 1) / (a + b + 2)) {
        return (bt * betaContinuedFraction(a, b, x)) / a;
    }
    return 1 - (bt * betaContinuedFraction(b, a, 1 - x)) / b;
}
/** Log of the beta density at x (for Newton steps in betaQuantile). */
function betaLogPdf(x, a, b) {
    return (logGamma(a + b) -
        logGamma(a) -
        logGamma(b) +
        (a - 1) * Math.log(x) +
        (b - 1) * Math.log(1 - x));
}
/**
 * Inverse regularized incomplete beta: the x with I_x(a, b) = p
 * (the brief's BetaInv; R's qbeta). Closed forms for a == 1 or b == 1;
 * otherwise bracketed Newton iteration to ~1e-13.
 */
export function betaQuantile(p, a, b) {
    if (p <= 0)
        return 0;
    if (p >= 1)
        return 1;
    if (a === 1 && b === 1)
        return p;
    if (b === 1)
        return Math.pow(p, 1 / a);
    if (a === 1)
        return 1 - Math.pow(1 - p, 1 / b);
    let lo = 0;
    let hi = 1;
    // Initial guess: the beta mean.
    let x = a / (a + b);
    if (x <= 0 || x >= 1)
        x = 0.5;
    let best = x;
    let bestErr = Infinity;
    for (let iter = 0; iter < 300; iter++) {
        const cdf = betaCdf(x, a, b);
        const err = Math.abs(cdf - p);
        if (err < bestErr) {
            bestErr = err;
            best = x;
        }
        if (err <= 1e-13)
            return x;
        if (cdf < p)
            lo = Math.max(lo, x);
        else
            hi = Math.min(hi, x);
        if (hi - lo < 2e-14)
            return (lo + hi) / 2;
        // Newton step when the density is usable; a forced bisection every
        // fourth iteration guarantees the bracket keeps halving even where the
        // density is near-singular (shape parameters below 1).
        const pdf = Math.exp(betaLogPdf(x, a, b));
        let next = Number.isFinite(pdf) && pdf > 0 ? x - (cdf - p) / pdf : NaN;
        if (!(next > lo && next < hi) || iter % 4 === 3)
            next = (lo + hi) / 2;
        x = next;
    }
    return best;
}
/**
 * Inverse Student t CDF for df >= 1, via the t/beta relation:
 * for t > 0, P(T > t) = 0.5 * I_{df/(df+t^2)}(df/2, 1/2).
 */
export function tQuantile(p, df) {
    if (p <= 0)
        return -Infinity;
    if (p >= 1)
        return Infinity;
    if (p === 0.5)
        return 0;
    if (p < 0.5)
        return -tQuantile(1 - p, df);
    const z = betaQuantile(2 * (1 - p), df / 2, 0.5);
    return Math.sqrt((df * (1 - z)) / z);
}
/** logit and its inverse (brief 9.3). */
export function logit(p) {
    return Math.log(p / (1 - p));
}
export function inverseLogit(x) {
    if (x >= 0)
        return 1 / (1 + Math.exp(-x));
    const e = Math.exp(x);
    return e / (1 + e);
}
export function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
/**
 * Gauss-Legendre nodes and weights on [a, b] with n nodes: roots of
 * the Legendre polynomial P_n by Newton iteration from the standard
 * cosine initial guess, weights 2 / ((1 - x^2) P'_n(x)^2), mapped
 * affinely to [a, b]. Deterministic; used by the F-2 model class's
 * E-step quadrature (fayherriot-t.ts). n >= 1.
 */
export function gaussLegendre(n, a, b) {
    if (!Number.isInteger(n) || n < 1) {
        throw new Error(`gaussLegendre: n must be a positive integer; received ${n}.`);
    }
    const xs = new Array(n);
    const ws = new Array(n);
    const mid = 0.5 * (a + b);
    const half = 0.5 * (b - a);
    // P_n(x) and P_{n-1}(x) by the three-term recurrence; the derivative
    // follows from (1 - x^2) P'_n = n (P_{n-1} - x P_n), used in the
    // equivalent form P'_n = n (x P_n - P_{n-1}) / (x^2 - 1).
    const legendre = (x) => {
        let pPrev = 1; // P_0
        let pCur = x; // P_1
        if (n === 0)
            return { pn: 1, dpn: 0 };
        for (let k = 2; k <= n; k++) {
            const pNext = ((2 * k - 1) * x * pCur - (k - 1) * pPrev) / k;
            pPrev = pCur;
            pCur = pNext;
        }
        return { pn: pCur, dpn: (n * (x * pCur - pPrev)) / (x * x - 1) };
    };
    for (let i = 0; i < Math.ceil(n / 2); i++) {
        // Initial guess for the (i+1)-th root, counting from +1 down.
        let x = Math.cos((Math.PI * (i + 0.75)) / (n + 0.5));
        for (let it = 0; it < 100; it++) {
            const { pn, dpn } = legendre(x);
            const dx = -pn / dpn;
            x += dx;
            if (Math.abs(dx) < 1e-15)
                break;
        }
        const { dpn } = legendre(x);
        const w = 2 / ((1 - x * x) * dpn * dpn);
        xs[i] = mid - half * x;
        xs[n - 1 - i] = mid + half * x;
        ws[i] = half * w;
        ws[n - 1 - i] = half * w;
    }
    return { nodes: xs, weights: ws };
}
//# sourceMappingURL=numeric.js.map